import { Navigate } from 'react-router-dom';

import { Feature } from '@occc/utils-access-control';
import config from '@occc/config';

import {
  RewardProgram,
  Site_System_Details_Protocol,
  SiteManagementLocationFragment,
} from '@occc/graphql';
import { isEmpty } from 'lodash';
import { ILocationDetailsRouteDescription } from './types';
import LocationSiteControllerStatus from './LocationSiteControllerStatus';
import { LocationUpdateDetailsForm } from './LocationUpdateDetailsForm';
import LocationScanDataDetails, { LocationScanDataEdit } from './LocationScanData';
import LocationTestingDashboard from './location-testing-dashboard';
import LocationActivityLog from './location-activity-log';
import { LocationGeneralDetailsEdit } from './LocationGeneralDetailsEdit';

import { LocationConnectionHistoryContainer as ConnectionHistory } from './location-connection-history';
import { routeCreator, tabValues } from '../utils';
import { TITLE_BY_LOYALTY_PROVIDER } from '../constants';

export const locationUpdateRoutes: ILocationDetailsRouteDescription[] = [
  {
    path: '',
    label: 'general details',
    component: () => <Navigate to={tabValues.generalDetails} replace />,
    feature: [Feature.LOCATION_EDIT, Feature.LOCATION_EDIT_VIEW_ONLY],
    skipInTabs: true,
  },
  {
    path: tabValues.generalDetails,
    label: 'general details',
    component: LocationUpdateDetailsForm,
    feature: [Feature.LOCATION_EDIT, Feature.LOCATION_EDIT_VIEW_ONLY],
  },
  {
    path: `${tabValues.generalDetails}/update`,
    label: 'edit general details',
    component: LocationGeneralDetailsEdit,
    feature: Feature.LOCATION_EDIT,
    skipInTabs: true,
  },
  ...(config.features.scanDataTabEnabled
    ? [
        {
          path: tabValues.scanData,
          label: 'tobacco',
          component: LocationScanDataDetails,
          feature: [Feature.LOCATION_EDIT, Feature.LOCATION_EDIT_VIEW_ONLY],
        },
        {
          path: `${tabValues.scanData}/update`,
          label: 'edit scan data',
          component: LocationScanDataEdit,
          feature: Feature.LOCATION_EDIT,
          skipInTabs: true,
        },
      ]
    : []),
  {
    path: tabValues.siteControllerStatus,
    label: 'site controller status',
    component: LocationSiteControllerStatus,
    feature: [Feature.LOCATION_EDIT, Feature.LOCATION_EDIT_VIEW_ONLY],
  },
  {
    path: tabValues.activityLog,
    label: 'activity log',
    component: LocationActivityLog,
    feature: [Feature.LOCATION_EDIT, Feature.LOCATION_EDIT_VIEW_ONLY],
  },
  {
    path: tabValues.connectionHistory,
    label: 'connection history',
    component: ConnectionHistory,
    feature: [Feature.LOCATION_EDIT, Feature.LOCATION_EDIT_VIEW_ONLY],
  },
  ...(config.features.mpaSimulatorEnabled // Some of our vendors want to be able to disable the MPA Simulator
    ? [
        {
          path: tabValues.testingDashboard,
          label: 'testing dashboard',
          component: LocationTestingDashboard,
          feature: [Feature.LOCATION_EDIT, Feature.LOCATION_EDIT_VIEW_ONLY],
        },
      ]
    : []),
];

export const getBreadcrumbLinks = (locationName: string | undefined) => [
  { to: routeCreator.list(), label: 'Locations' },
  { key: routeCreator.generalDetails(':locationId'), label: locationName },
];

export const parseAttributeInput = (attributeInput: string): string[] => {
  const attributeList = attributeInput.split(',');

  return attributeList.reduce((attributesList, currentAttribute) => {
    const trimmedAttribute: string = currentAttribute.trim();

    if (isEmpty(trimmedAttribute)) return attributesList;

    return [...attributesList, trimmedAttribute];
  }, []);
};

export const getStatusLabel = ({
  protocol,
  version,
  isEnabled,
}: {
  protocol?: Site_System_Details_Protocol | null;
  version?: string | null;
  isEnabled: boolean;
}) => {
  const isEnabledStatus = isEnabled ? 'Enabled' : 'Disabled';

  if (protocol && version) {
    return `${protocol} ${version} ${isEnabledStatus}`;
  }

  return isEnabledStatus;
};

export const getLoyaltyHostLabel = (
  rewardPrograms?: (Pick<RewardProgram, 'uuid' | 'programId' | 'type'> | null)[] | null,
) => {
  if (!rewardPrograms) return '';

  return rewardPrograms
    .filter((program): program is RewardProgram => program !== null)
    .map((program) => TITLE_BY_LOYALTY_PROVIDER[program.type])
    .filter(Boolean)
    .join(', ');
};

export const getSiteSystemDetailsFromLocation = (data: SiteManagementLocationFragment | null) => {
  const details = data?.siteSystemConfiguration?.[0]?.details;

  return {
    loyaltyProtocol: details?.loyaltyProtocol,
    loyaltyVersion: details?.loyaltyVersion,
    mobileProtocol: details?.mobileProtocol,
    mobileVersion: details?.mobileVersion,
    mobileSiteSystem: details?.hardwareVendor || null,
  };
};
